import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import { CommonEntityService } from './common-entity.service';
import { LoggerService } from './logger.service';
import { UtilitiesService } from './utilities.service';

@Injectable({
  providedIn: 'root'
})
export class EndlessOfferService {

  constructor(
    private http: HttpClient,
    private commonEntityService: CommonEntityService,
    private logger: LoggerService,
    private messageService: MessageService,
    private utilities: UtilitiesService
  ) { }

  async getBundlesData(rewardTrack: any, setLoadingState: (isLoading: boolean) => void) {
    if (!rewardTrack.EObundles) {
      return;
    }

    const tasks: (() => Promise<void>)[] = [];

    for (const bundle of rewardTrack.EObundles) {
      if (bundle.type === 'paid' && bundle.listing && bundle.listing.Name) {
        const query = bundle.listing.Name;
        tasks.push(async () => {
          try {
            const result = await this.http.get<any>(`/api/endless-offer-track/store/listing?searchBy=${query}`).toPromise();
            if (result.storeListings && result.storeListings.length > 0) {
              const firstMatch = result.storeListings[0];
              if (firstMatch.Name === query) {
                bundle.listing = firstMatch;
              }
            }
          } catch (err) {
            this.logger.error('Error fetching bundle data:', err, 'EndlessOfferService');
          }
        });
      }
    }

    await this.utilities.runWithConcurrency(tasks, 6);
    setLoadingState(false);
  }

  async buildDisplayData(rewardTrack: any, stepIndex?: number) {
    if (!rewardTrack.EOrewards) {
      return;
    }

    const isTemplate = rewardTrack.isTemplate === true;
    const allTasks: (() => Promise<void>)[] = [];

    const rewards: any[] = stepIndex !== undefined
      ? [rewardTrack.EOrewards[stepIndex]]
      : rewardTrack.EOrewards;

    for (const [index, reward] of rewards.entries()) {
      // Skip if reward array is empty or reward[0] doesn't exist
      if (!reward || !reward[0]) {
        continue;
      }

      // pricePoint_ref and credits_ref - StoreListingV2 or DynamicMysteryBox (which links to StoreListingV2)
      const isStoreListing = reward[0].lineItemType === "StoreListingV2";
      const isDynamicMysteryBox = reward[0].lineItemType === "DynamicMysteryBox";
      const rewardSource = isStoreListing ? reward[0].id : (isDynamicMysteryBox && reward[0].id?.storeListing) ? reward[0].id.storeListing : null;

      if ((isStoreListing || isDynamicMysteryBox) && rewardSource) {
        // For templates, skip processing if id is null/undefined (empty store listings allowed)
        // For non-templates, id should always exist, but still check to prevent errors
        if (!reward[0].id && !isDynamicMysteryBox) {
          if (isTemplate) {
            continue;
          } else {
            console.warn('Store listing id is missing for non-template track');
            this.messageService.add({
              severity: 'warn',
              summary: 'Missing Store Listing',
              detail: 'Store listing ID is missing for a non-template track. Please select a store listing.',
              life: 5000
            });
            continue;
          }
        }

        if (isDynamicMysteryBox && !reward[0].id?.storeListing) {
          continue; // DynamicMysteryBox without store listing - skip price/credits
        }

        // Queue price point fetch
        if (rewardSource?.pricePoint_ref?.qa) {
          allTasks.push(async () => {
            try {
              const pricePoint = await this.commonEntityService.findOneWithQuery('price-points', {
                query: { _id: rewardSource.pricePoint_ref.qa },
                smartPopulate: { "thumbnail_ref": "_id id name thumbnails", "image_ref": "_id id name thumbnails", "box_image_ref": "_id id name thumbnails" }
              });
              reward[0].pricePoint = pricePoint;
            } catch (err) {
              this.logger.error('Error fetching price point data:', err, 'EndlessOfferService');
            }
          });
        }

        // Queue credit fetches
        const creditsToProcess = rewardSource?.credits_ref?.qa;
        if (creditsToProcess) {
          for (const credit of creditsToProcess) {
            const { entity, entityNumber } = this.getEntityDetails(credit.t.name);
            allTasks.push(async () => {
              try {
                const resourceData = await this.fetchResourceData(this.commonEntityService, entity, credit.id.id);
                credit.entity = entity;
                credit.link = `${entity}/${credit.id.id}`;
                credit.displayName = resourceData.name || 'undefined';
                credit.thumbnail = this.getThumbnail(resourceData, entityNumber);
              } catch (err) {
                this.logger.error('Error fetching entity data:', err, 'EndlessOfferService');
              }
            });
          }
        }
      } else {
        this.logger.debug('Free reward:', rewardTrack?.rewards?.[index], 'EndlessOfferService');
      }
    }

    await this.utilities.runWithConcurrency(allTasks, 6);
    return rewardTrack.EOrewards;
  }

  getEntityDetails(name: any) {
    switch (name) {
      case 'Currency':
        return { entity: 'currencies', entityNumber: 1 };
      case 'Item':
        return { entity: 'items', entityNumber: 2 };
      case 'SeedPackBox':
        return { entity: 'seed-pack-box', entityNumber: 3 };
      case 'DynamicMysteryBox':
        return { entity: 'dynamic-mystery-box', entityNumber: 5 };
      default:
        return { entity: '', entityNumber: 0 };
    }
  }

  async fetchResourceData(service: any, entity: any, id: any) {
    const basePopulate = { "thumbnail_ref": "_id id name thumbnails", "image_ref": "_id id name thumbnails", "box_image_ref": "_id id name thumbnails" };
    const dmBoxPopulate = { ...basePopulate, "asset3D_ref": "_id id name thumbnails" };
    const smartPopulate = entity === 'dynamic-mystery-box' ? dmBoxPopulate : basePopulate;
    return service.findOneWithQuery(entity, {
      query: { id },
      smartPopulate
    });
  }

  getThumbnail(resourceData: any, entityNumber: any) {
    let thumbnail = 'https://d3tfb94dc03jqa.cloudfront.net/thumbnails/thumbnail_placeholder.jpeg';

    if (entityNumber === 1 && resourceData.image_ref && resourceData.image_ref.thumbnails && resourceData.image_ref.thumbnails[1]) {
      thumbnail = resourceData.image_ref.thumbnails[1].path;
    } else if (entityNumber === 2 && resourceData.thumbnail_ref && resourceData.thumbnail_ref.thumbnails && resourceData.thumbnail_ref.thumbnails[1]) {
      thumbnail = resourceData.thumbnail_ref.thumbnails[1].path;
    } else if (entityNumber === 3 && resourceData.box_image_ref && resourceData.box_image_ref.thumbnails && resourceData.box_image_ref.thumbnails[1]) {
      thumbnail = resourceData.box_image_ref.thumbnails[1].path;
    } else if (entityNumber === 5 && resourceData.asset3D_ref?.thumbnails?.[1]) {
      thumbnail = resourceData.asset3D_ref.thumbnails[1].path;
    }

    return thumbnail;
  }

  /**
   * Initialize endless offer track record from parent field data
   * Fetches full track data including EOrewards and EObundles from the API
   * 
   * @param parentFieldData - Could be an _id string or a populated object
   * @param options - Array of track options to search for matching track
   * @param endlessOfferTrackRecord - The record to populate with track data
   */
  async initializeTrackFromParentData(
    parentFieldData: any,
    options: any[],
    endlessOfferTrackRecord: any
  ): Promise<void> {
    if (!parentFieldData) {
      return;
    }

    const trackId = typeof parentFieldData === 'object' ? parentFieldData._id : parentFieldData;
    const trackIdStr = String(trackId);

    const matchingTrack = options.find(option => String(option._id) === trackIdStr);
    const numericId = matchingTrack?.id || (typeof parentFieldData === 'object' ? parentFieldData.id : null);

    if (numericId) {
      try {
        const fullTrack = await this.http.get<any>(`/api/rewards-track/full/${numericId}`).toPromise();
        if (fullTrack) {
          endlessOfferTrackRecord['_id'] = fullTrack._id;
          endlessOfferTrackRecord['EOrewards'] = fullTrack.EOrewards || [];
          endlessOfferTrackRecord['EObundles'] = fullTrack.EObundles || [];
          endlessOfferTrackRecord['isLoop'] = fullTrack.isLoop;
        }
      } catch (error) {
        this.logger.error('Failed to fetch full rewards track data:', error, 'EndlessOfferService');
        // Fall back to dropdown options if API call fails
        if (matchingTrack) {
          endlessOfferTrackRecord['_id'] = matchingTrack._id;
          if (matchingTrack.EOrewards) {
            endlessOfferTrackRecord['EOrewards'] = matchingTrack.EOrewards;
          }
          if (matchingTrack.EObundles) {
            endlessOfferTrackRecord['EObundles'] = matchingTrack.EObundles;
          }
        }
      }
    } else if (matchingTrack) {
      endlessOfferTrackRecord['_id'] = matchingTrack._id;
      if (matchingTrack.EOrewards) {
        endlessOfferTrackRecord['EOrewards'] = matchingTrack.EOrewards;
      }
      if (matchingTrack.EObundles) {
        endlessOfferTrackRecord['EObundles'] = matchingTrack.EObundles;
      }
    }
  }

}
